/**
 * Workflow v2 AppShell — 3-zone master/detail layout
 * (StepRail / center content / ActionPanel) for the redesigned TARA
 * workflow tab inside ProjectWorkspace.
 *
 * Differences from the source mockup:
 *   - The mockup's Header (breadcrumbs + tab strip) is dropped —
 *     ProjectWorkspace already provides project header + sub-nav.
 *   - The mockup's DemoControls floating widget is dropped (demo-
 *     only state-machine helper).
 *   - Sizing is `flex: 1; min-height: 0` against ProjectWorkspace's
 *     bounded tab content area so StepShell's inner content region
 *     owns scroll, not the page.
 *
 * Panel collapse is owned here because collapsing means UNMOUNTING the whole
 * ActionPanel so the centre reclaims the width — a panel that hides its own
 * contents still holds the column, which is worse than leaving it open. One
 * state, one control: the subheader's right PanelToggle. It is driven by the
 * user AND automatically while the step's agent runs.
 */
import React, { useEffect, useRef, useState } from 'react';
import WorkflowV2StepRail from './WorkflowV2StepRail.jsx';
import WorkflowV2ActionPanel from './WorkflowV2ActionPanel.jsx';
import PanelToggle from './PanelToggle.jsx';
import { useSubHeaderSlot } from '../../projects/WorkspaceChromeContext';
import styles from './WorkflowV2AppShell.module.css';

export default function WorkflowV2AppShell({
  state,
  actions,
  onApprove,
  approving,
  onResume,
  resuming,
  approveHidden,
  panelHidden,
  threadId,
  refetchStatus,
  chat,
  children,
}) {
  const [railCollapsed, setRailCollapsed] = useState(false);

  // The two backend in_progress states: `generating` is a step's first run
  // (entered by approving the previous step), `revising` a re-run after
  // feedback. While either is true the panel has nothing to offer — the centre
  // pane's progress display is what the user is watching — so the whole panel
  // collapses and the centre reclaims the width.
  const selectedStepId = state?.selectedStepId;
  const stepStatus = state?.steps?.[selectedStepId]?.status;
  const agentBusy = stepStatus === 'generating' || stepStatus === 'revising';

  // Seeded from `agentBusy` so arriving at an already-running step starts
  // collapsed rather than flashing open.
  const [panelCollapsed, setPanelCollapsed] = useState(agentBusy);

  // What the user last chose, so a run restores their preference instead of
  // unconditionally reopening — a run must not silently undo a deliberate
  // collapse. Updated by the toggle, whenever it is clicked.
  const userPrefersCollapsedRef = useRef(agentBusy);
  const togglePanel = () =>
    setPanelCollapsed((v) => {
      userPrefersCollapsedRef.current = !v;
      return !v;
    });

  // Edge-detected on (step, busy) rather than derived from `agentBusy`, so a
  // manual toggle mid-run sticks instead of snapping back on the next render.
  const busyEdgeRef = useRef(`${selectedStepId}:${agentBusy}`);
  useEffect(() => {
    const edge = `${selectedStepId}:${agentBusy}`;
    if (busyEdgeRef.current === edge) return;
    busyEdgeRef.current = edge;
    setPanelCollapsed(agentBusy ? true : userPrefersCollapsedRef.current);
  }, [selectedStepId, agentBusy]);

  // Teleport the rail/panel toggles into the subheader's row-2 ends
  // (WorkspaceChromeContext). The collapse state stays owned here; only the
  // buttons render up in the bar. No-op if no WorkspaceChromeProvider is
  // mounted (e.g. isolated tests).
  useSubHeaderSlot(
    'lead',
    <PanelToggle
      side="left"
      collapsed={railCollapsed}
      onToggle={() => setRailCollapsed((v) => !v)}
    />,
    [railCollapsed],
  );
  // The right panel is gated by the step-driven `panelHidden` first; only when
  // the panel is logically available do we offer the user a collapse toggle.
  useSubHeaderSlot(
    'trail',
    panelHidden ? null : (
      <PanelToggle
        side="right"
        collapsed={panelCollapsed}
        onToggle={togglePanel}
      />
    ),
    [panelHidden, panelCollapsed],
  );

  return (
    <div className={styles.shell}>
      <main className={styles.main}>
        {!railCollapsed && (
          <WorkflowV2StepRail state={state} actions={actions} />
        )}
        <section className={styles.center}>
          {children}
        </section>
        {!panelHidden && !panelCollapsed && (
          <WorkflowV2ActionPanel
            state={state}
            actions={actions}
            onApprove={onApprove}
            approving={approving}
            onResume={onResume}
            resuming={resuming}
            approveHidden={approveHidden}
            threadId={threadId}
            refetchStatus={refetchStatus}
            chat={chat}
          />
        )}
      </main>
    </div>
  );
}
